import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { AuthService } from '@features/auth/services/auth.service';
import { ProductDiscussion } from '@features/product/interfaces/product-discussion.interface';
import { ProductDiscussionService } from '@features/product/services/product-discussion.service';
import { User } from '@features/user/interfaces/user.interface';
import { UserService } from '@features/user/services/user.service';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';

@Component({
  selector: 'fc-product-detail-discussion',
  templateUrl: './product-detail-discussion.component.html',
  styleUrls: ['./product-detail-discussion.component.css'],
})
export class ProductDetailDiscussionComponent implements OnInit {
  faSortDown = faSortDown;
  faSortUp = faSortUp;

  @Input() slug: any;
  @Input() productId: any;

  totalDiscussion: number = 0;
  discussions?: ProductDiscussion[] = [];
  user?: User;
  userDefaultPhoto?: any;

  newDiscussion: any = null;
  newReplies: any = [];
  loadedRepliesTotal: any = [];
  loadedRepliesCurrentPage: any = [];

  addDiscussionLoading = false;
  addReplyLoading = false;

  @ViewChild('discussionTop') discussionTop?: ElementRef;

  userSubscription?: Subscription;
  tokenSubscription?: Subscription;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private productDiscussionService: ProductDiscussionService,
    private userService: UserService,
    private authService: AuthService
  ) {}

  ngOnChanges() {
    this.productDiscussionService
      .getProductDiscussions(this.slug, 5, 1)
      .subscribe((data: any) => {
        this.totalDiscussion = data.total_items;
        this.discussions = data.product_discussions;
        this.newReplies = data.product_discussions.map((dcs: any) => '');
        this.loadedRepliesTotal = data.product_discussions.map((dcs: any) => 0);
        this.loadedRepliesCurrentPage = data.product_discussions.map(
          (dcs: any) => 1
        );
      });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.tokenSubscription = this.authService
        .userTokenEmitter()
        .subscribe((token: any) => {
          if (token)
            this.userSubscription = this.userService
              .getUserData()
              .subscribe((data) => (this.user = data));
        });
    }
  }

  userErrorImg() {
    this.userDefaultPhoto = 'assets/images/default-user.png';
  }

  pageChange(page: number) {
    this.loadProductDiscussions(page);
    this.onScrollToDiscussionTop();
  }

  loadProductDiscussions(currentPage: number) {
    this.productDiscussionService
      .getProductDiscussions(this.slug, 5, currentPage)
      .subscribe((data: any) => {
        this.discussions = data.product_discussions;
        this.newReplies = data.product_discussions.map((dcs: any) => '');
        this.loadedRepliesTotal = data.product_discussions.map((dcs: any) => 0);
        this.loadedRepliesCurrentPage = data.product_discussions.map(
          (dcs: any) => 1
        );
      });
  }

  showReplies(discussion: ProductDiscussion, index: number) {
    if (discussion.showReplies) {
      discussion.showReplies = false;
    } else {
      discussion.showReplies = true;
      if (!discussion.replyData) {
        this.productDiscussionService
          .getProductDiscussionReplies(discussion.id, 10, 1)
          .subscribe((data) => {
            discussion.replyData = data;
            if (index) {
              this.loadedRepliesTotal[index] +=
                data.product_discussion_replies.length;
            }
          });
      }
    }
  }

  showMoreReplies(discussion: ProductDiscussion, index: number) {
    this.loadedRepliesCurrentPage[index] += 1;
    this.productDiscussionService
      .getProductDiscussionReplies(
        discussion.id,
        10,
        this.loadedRepliesCurrentPage[index]
      )
      .subscribe((data) => {
        data.product_discussion_replies.forEach((reply) => {
          discussion.replyData.product_discussion_replies.push(reply);
        });
        if (index) {
          this.loadedRepliesTotal[index] +=
            data.product_discussion_replies.length;
        }
      });
  }

  showLessReplies(discussion: ProductDiscussion, index: number) {
    discussion.replyData.product_discussion_replies =
      discussion.replyData.product_discussion_replies.slice(0, 10);
    this.loadedRepliesTotal[index] = 10;
    this.loadedRepliesCurrentPage[index] = 1;
    this.onScrollToDiscussion(document.getElementById(`${index}`));
  }

  sendProductDiscussion() {
    if (!this.addDiscussionLoading) {
      this.addDiscussionLoading = true;
      if (this.newDiscussion != null || this.newDiscussion != '') {
        this.productDiscussionService
          .addProductDiscussion(this.productId, this.newDiscussion)
          .subscribe((data) => {
            this.discussions?.unshift(data);
            this.newDiscussion = null;
            this.totalDiscussion = this.totalDiscussion
              ? this.totalDiscussion + 1
              : 1;
            this.addDiscussionLoading = false;
          });
      }
    }
  }

  sendDiscussionReply(discussion: ProductDiscussion, index: any) {
    if (!this.addReplyLoading) {
      this.addReplyLoading = true;
      if (this.newReplies[index] != null || this.newReplies[index] != '') {
        this.productDiscussionService
          .addProductDiscussionReply(
            this.productId,
            discussion.id,
            this.newReplies[index]
          )
          .subscribe((data) => {
            let updatedDiscussion: any = this.discussions?.find(
              (dsc) => dsc.id === discussion.id
            );
            updatedDiscussion.total_replies += 1;
            updatedDiscussion.showSendReply = false;
            discussion.replyData.product_discussion_replies.push(data);
            this.newReplies[index] = null;
            this.addReplyLoading = false;
          });
      }
    }
  }

  onScrollToDiscussionTop() {
    let headerOffset = 200;
    let offsetPosition =
      this.discussionTop?.nativeElement.offsetTop - headerOffset;
    window.scrollTo({ top: offsetPosition });
  }

  onScrollToDiscussion(elem: any) {
    let headerOffset = 200;
    let offsetPosition = elem.offsetTop - headerOffset;
    window.scrollTo({ top: offsetPosition });
  }

  ngOnDestroy() {
    if (this.userSubscription) this.userSubscription.unsubscribe();
    if (this.tokenSubscription) this.tokenSubscription.unsubscribe();
  }
}
